import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, FileText, Users, Settings, ChevronDown, Sun, Moon, Palette, HelpCircle, User, ExternalLink, LogOut } from 'lucide-react';
import KBIcon from './KBIcon';
import { useKB } from './KBContext';
import { useVisualTheme } from './ThemeContext';
import { libraryRoot, libraryHelpUrl } from '../../utils/hostMode';

const IPMI_HOME_URL = process.env.REACT_APP_IPMI_HOME_URL || 'https://ipmi.org';

/**
 * TopNav — persistent header bar.
 * Replaces the old left-sidebar brand block. The left sidebar now only carries filters
 * (and only renders on the Documents tab).
 */
const TopNav = () => {
  const { activeTab, setActiveTab, lightMode, setLightMode,
          memberInfo, memberLogout, staffLogout, setShowProfileModal, setAdminFocus } = useKB();
  const { visualTheme, setVisualTheme } = useVisualTheme();

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Compute display initials for member avatar (else fallback to IPMI)
  const initials = memberInfo?.full_name
    ? memberInfo.full_name.trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase()
    : 'IPMI';
  const firstName = memberInfo?.full_name ? memberInfo.full_name.split(/\s+/)[0] : null;

  // Close dropdown on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    if (menuOpen) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [menuOpen]);

  const tabs = [
    { id: 'chat', label: 'Chat', Icon: MessageSquare },
    { id: 'documents', label: 'Documents', Icon: FileText },
    { id: 'members', label: 'Members', Icon: Users, badge: 'PREVIEW' },
  ];

  return (
    <header className="kb-topnav" data-testid="kb-topnav">
      <div className="kb-topnav-left">
        <a href={libraryRoot()} className="kb-topnav-brand" data-testid="kb-topnav-brand">
          <KBIcon size={28} className="kb-topnav-icon" />
          <span className="kb-topnav-title">IPMI Library</span>
          <span className="kb-topnav-beta" data-testid="kb-beta-badge">BETA</span>
        </a>
      </div>

      <nav className="kb-topnav-tabs" data-testid="kb-topnav-tabs">
        {tabs.map(({ id, label, Icon, comingSoon, badge }) => (
          <button
            key={id}
            className={`kb-topnav-tab ${activeTab === id ? 'active' : ''} ${comingSoon ? 'disabled' : ''}`}
            onClick={() => !comingSoon && setActiveTab(id)}
            disabled={comingSoon}
            title={comingSoon ? 'Coming soon — Phase 3' : label}
            data-testid={`kb-topnav-tab-${id}`}
          >
            <Icon size={16} />
            <span>{label}</span>
            {comingSoon && <span className="kb-topnav-soon">soon</span>}
            {badge && !comingSoon && <span className="kb-topnav-soon">{badge}</span>}
          </button>
        ))}
      </nav>

      <div className="kb-topnav-right">
        <a
          href={IPMI_HOME_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="kb-topnav-home-link"
          data-testid="kb-topnav-home-link"
          title="Back to IPMI home"
        >
          <ExternalLink size={12} />
          <span>IPMI Home</span>
        </a>

        <div className="kb-topnav-account" ref={menuRef}>
          <button
            className="kb-topnav-avatar"
            onClick={() => setMenuOpen(!menuOpen)}
            data-testid="kb-account-trigger"
            aria-label="Open account menu"
            title={memberInfo?.full_name || 'IPMI Library'}
          >
            <span className="kb-topnav-avatar-initials">{initials}</span>
            <ChevronDown size={14} className={`kb-topnav-chev ${menuOpen ? 'open' : ''}`} />
          </button>

          <AnimatePresence>
            {menuOpen && (
              <motion.div
                className="kb-topnav-menu"
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.15 }}
                data-testid="kb-account-menu"
              >
                <div className="kb-topnav-menu-section">
                  <div className="kb-topnav-menu-label">Account</div>
                  {memberInfo ? (
                    <>
                      <div
                        className="kb-topnav-menu-member"
                        data-testid="kb-account-member-card"
                        style={{
                          padding: '10px 14px',
                          fontSize: 13,
                          lineHeight: 1.4,
                          color: 'var(--kb-fg)',
                        }}
                      >
                        <div style={{ fontWeight: 600 }}>{memberInfo.full_name}</div>
                        {memberInfo.position_title && (
                          <div style={{ fontSize: 12, color: 'var(--kb-fg-soft, #8B96A5)', marginTop: 2 }}>
                            {memberInfo.position_title}
                          </div>
                        )}
                        {memberInfo.company_name && (
                          <div style={{ fontSize: 12, color: 'var(--kb-fg-soft, #8B96A5)' }}>
                            {memberInfo.company_name}
                          </div>
                        )}
                        <div style={{ fontSize: 11, color: 'var(--kb-fg-soft, #8B96A5)', marginTop: 4 }}>
                          {memberInfo.email}
                        </div>
                      </div>
                      <button
                        className="kb-topnav-menu-item"
                        onClick={() => { setShowProfileModal(true); setMenuOpen(false); }}
                        data-testid="kb-menu-edit-profile"
                      >
                        <User size={14} /> Edit Profile
                      </button>
                    </>
                  ) : (
                    <button
                      className="kb-topnav-menu-item"
                      onClick={() => { setActiveTab('admin'); setAdminFocus('org-profile'); setMenuOpen(false); }}
                      data-testid="kb-menu-admin-profile"
                    >
                      <User size={14} /> IPMI Profile
                    </button>
                  )}
                </div>

                <div className="kb-topnav-menu-divider" />

                <div className="kb-topnav-menu-section">
                  <div className="kb-topnav-menu-label">Appearance</div>
                  <button
                    className={`kb-topnav-menu-item ${visualTheme === 'classic' ? 'active' : ''}`}
                    onClick={() => { setVisualTheme('classic'); setMenuOpen(false); }}
                    data-testid="kb-theme-classic"
                  >
                    <Palette size={14} /> Classic
                    {visualTheme === 'classic' && <span className="kb-topnav-menu-check">✓</span>}
                  </button>
                  <button
                    className={`kb-topnav-menu-item ${visualTheme === 'modern' ? 'active' : ''}`}
                    onClick={() => { setVisualTheme('modern'); setMenuOpen(false); }}
                    data-testid="kb-theme-modern"
                  >
                    <Palette size={14} /> Modern
                    {visualTheme === 'modern' && <span className="kb-topnav-menu-check">✓</span>}
                  </button>
                  <button
                    className="kb-topnav-menu-item"
                    onClick={() => { setLightMode(!lightMode); setMenuOpen(false); }}
                  >
                    {lightMode ? <Moon size={14} /> : <Sun size={14} />}
                    {lightMode ? 'Switch to Dark' : 'Switch to Light'}
                  </button>
                </div>

                <div className="kb-topnav-menu-divider" />

                <div className="kb-topnav-menu-section">
                  <button
                    className="kb-topnav-menu-item"
                    onClick={() => { window.open(libraryHelpUrl(), '_blank'); setMenuOpen(false); }}
                    data-testid="kb-menu-help"
                  >
                    <HelpCircle size={14} /> How it Works
                  </button>
                  <a
                    href={IPMI_HOME_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="kb-topnav-menu-item"
                    onClick={() => setMenuOpen(false)}
                    data-testid="kb-menu-ipmi-home"
                  >
                    <ExternalLink size={14} /> Back to IPMI Home
                  </a>
                  {!memberInfo && (
                    <button
                      className={`kb-topnav-menu-item ${activeTab === 'admin' ? 'active' : ''}`}
                      onClick={() => { setActiveTab(activeTab === 'admin' ? 'chat' : 'admin'); setMenuOpen(false); }}
                      data-testid="kb-menu-admin"
                    >
                      <Settings size={14} /> Admin Tools
                    </button>
                  )}
                </div>

                {memberInfo && (
                  <>
                    <div className="kb-topnav-menu-divider" />
                    <div className="kb-topnav-menu-section">
                      <button
                        className="kb-topnav-menu-item"
                        onClick={() => { memberLogout(); setMenuOpen(false); }}
                        data-testid="kb-menu-logout"
                        style={{ color: '#EF4444' }}
                      >
                        <LogOut size={14} /> Sign out
                      </button>
                    </div>
                  </>
                )}
                {!memberInfo && (
                  <>
                    <div className="kb-topnav-menu-divider" />
                    <div className="kb-topnav-menu-section">
                      <button
                        className="kb-topnav-menu-item"
                        onClick={() => { staffLogout(); setMenuOpen(false); }}
                        data-testid="kb-menu-staff-logout"
                        style={{ color: '#EF4444' }}
                      >
                        <LogOut size={14} /> Sign out
                      </button>
                    </div>
                  </>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </header>
  );
};

export default TopNav;
